import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiGlobe, FiMapPin, FiUsers, FiSearch, FiFilter, FiBriefcase, FiToggleLeft, FiToggleRight, FiChevronRight } = FiIcons;

const CorporateOverview = () => {
  const navigate = useNavigate();
  const { departments = [], updateDepartment } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');

  // Filter logic med defensiv sjekk for hvert element
  const filteredDepartments = (departments || []).filter(dept => {
    if (!dept) return false;
    const matchesSearch = (dept.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (dept.manager || dept.manager_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = regionFilter === 'all' || dept.region === regionFilter;
    return matchesSearch && matchesRegion;
  });

  // Unique regions filter med defensiv sjekk
  const regions = ['all', ...new Set((departments || []).filter(d => d && d.region).map(d => d.region))];

  const handleToggleActive = (e, dept) => {
    e.stopPropagation();
    if (typeof updateDepartment === 'function') {
      updateDepartment(dept.id, { active: !dept.active });
    }
  };

  const handleGoToDashboard = (deptId) => {
    navigate(`/superadmin/department/${deptId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <SafeIcon icon={FiArrowLeft} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Land & Avdelinger</h1>
                <p className="text-sm text-gray-500">Global konsernstruktur</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="relative flex-1 w-full">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk etter land eller leder..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <SafeIcon icon={FiFilter} className="text-gray-400" />
            <select 
              value={regionFilter} 
              onChange={(e) => setRegionFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle regioner</option>
              {regions.filter(r => r !== 'all').map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Countries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDepartments.map((dept, index) => (
            <motion.div
              key={dept.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => handleGoToDashboard(dept.id)}
              className={`bg-white rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer border flex flex-col h-full group overflow-hidden ${dept.active ? 'border-gray-100' : 'border-red-100 bg-red-50/30'}`}
            >
              {/* 1. HEADER I KORT */}
              <div className="p-5 border-b border-gray-50 flex justify-between items-start">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-bold text-lg truncate ${dept.active ? 'text-gray-900' : 'text-red-900'}`}>{dept.name}</h3>
                    {dept.type === 'headquarters' && (
                      <span className="bg-blue-600 text-white text-[9px] font-black uppercase px-1.5 py-0.5 rounded flex-shrink-0">HQ</span>
                    )}
                  </div>
                  <p className="text-[10px] font-mono text-gray-400 uppercase tracking-tight truncate">ID: {dept.id}</p>
                </div>
                <button 
                  onClick={(e) => handleToggleActive(e, dept)} 
                  className={`text-2xl transition-colors flex-shrink-0 ${dept.active ? 'text-green-500 hover:text-green-600' : 'text-gray-300 hover:text-gray-400'}`}
                  title={dept.active ? 'Deaktiver' : 'Aktiver'}
                >
                  <SafeIcon icon={dept.active ? FiToggleRight : FiToggleLeft} />
                </button>
              </div>

              {/* 2. INNHOLD I KORT */}
              <div className="p-5 flex-grow space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <SafeIcon icon={FiUsers} size={16} className="text-gray-400" />
                    <span>Ansatte</span>
                  </div>
                  <span className="font-bold text-gray-900">{dept.employeeCount || dept.employee_count || 0}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <SafeIcon icon={FiBriefcase} size={16} className="text-gray-400" />
                    <span>Ansvarlig leder</span>
                  </div>
                  <span className="font-medium text-gray-900 truncate max-w-[140px]" title={dept.manager || dept.manager_name}>
                    {dept.manager || dept.manager_name || 'Ikke satt'}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-400 pt-1">
                  <SafeIcon icon={FiMapPin} size={12} />
                  <span className="truncate">{dept.region || 'Global'}</span>
                </div>
              </div>

              {/* 3. FOOTER I KORT */}
              <div className={`px-5 py-3 border-t flex justify-between items-center ${dept.active ? 'bg-gray-50/50' : 'bg-red-50/50'}`}>
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${dept.active ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`}></div>
                  <span className={`text-[10px] font-black uppercase tracking-wider ${dept.active ? 'text-green-700' : 'text-red-700'}`}>
                    {dept.active ? 'Operativ' : 'Deaktivert'}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-blue-600 text-xs font-bold uppercase tracking-tight group-hover:translate-x-1 transition-transform">
                  Dashboard <SafeIcon icon={FiChevronRight} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredDepartments.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <SafeIcon icon={FiGlobe} className="mx-auto text-4xl mb-4 opacity-20" />
            <p>Ingen land funnet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorporateOverview;